import React, { useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import ExplanationBox from '../shared/ExplanationBox';
import GuardrailWarning from '../shared/GuardrailWarning';
import Tooltip from '../shared/Tooltip';
import { calculateDefaults } from '../../utils/decisionLogic';

const Step2_TheoryApproach = () => {
  const { data, updateData, warnings, explanations } = useWizard();
  
  useEffect(() => {
    if (!data.theoryApproach) {
      const defaults = calculateDefaults(data.intake);
      if (defaults.theoryApproach) {
        updateData('theoryApproach', defaults.theoryApproach);
      }
    }
  }, []);
  
  const approaches = [
    { 
      value: 'deductive', 
      label: 'Deductive',
      description: 'Start with existing theory/hypotheses → Test with data → Confirm or refute theory',
      process: 'Theory → Hypothesis → Data Collection → Analysis → Confirmation/Rejection',
      bestFor: 'Testing theories, hypothesis testing, confirming relationships, quantitative research'
    },
    { 
      value: 'inductive', 
      label: 'Inductive',
      description: 'Start with data → Identify patterns → Build theory from findings',
      process: 'Observations → Patterns → Tentative hypothesis → Theory development',
      bestFor: 'Generating theory, exploratory research, qualitative research, grounded theory'
    },
    { 
      value: 'abductive', 
      label: 'Abductive',
      description: 'Iterate between data and theory → Refine understanding through cycles',
      process: 'Observation → Best explanation → Test → Refine → Theory emerges',
      bestFor: 'Mixed methods, pragmatic research, complex phenomena, iterative designs'
    }
  ];
  
  return (
    <div>
      <h2 className="step-title">Step 4: Approach to Theory Development</h2>
      <p className="step-description">
        How will theory relate to your study?
        <Tooltip text="This determines whether you're testing existing theories (deductive), building new theory from data (inductive), or moving back and forth between data and theory (abductive)." />
      </p>
      
      {explanations.recommendation && (
        <ExplanationBox title="Recommended based on your choices">
          <p>{explanations.recommendation}</p>
        </ExplanationBox>
      )}
      
      <div className="form-group">
        <label className="form-label">Select your approach to theory:</label>
        <div className="option-cards theory-cards">
          {approaches.map((approach, index) => (
            <div 
              key={approach.value}
              className={`option-card ${data.theoryApproach === approach.value ? 'selected' : ''} ${index < approaches.length - 1 ? 'with-separator' : ''}`}
              onClick={() => updateData('theoryApproach', approach.value)}
            >
              <div className="option-header">
                <input 
                  type="radio"
                  name="theoryApproach"
                  value={approach.value}
                  checked={data.theoryApproach === approach.value}
                  onChange={(e) => updateData('theoryApproach', e.target.value)}
                />
                <span className="option-title"><strong>{approach.label}</strong></span>
              </div>
              <p className="option-description" style={{ marginBottom: '0.75rem' }}>
                {approach.description}
              </p>
              <p className="option-description theory-process">
                <strong>Process:</strong> {approach.process}
              </p>
              <p className="option-description" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                <strong>Best for:</strong> {approach.bestFor}
              </p>
            </div>
          ))}
        </div>
      </div>
      
      <div className="info-box">
        <div className="info-title">💡 Alignment Check</div>
        <div className="info-content">
          <p>Your current philosophy: <strong>{data.philosophy || 'Not selected'}</strong></p>
          <p style={{ marginTop: '0.5rem' }}>
            Typical alignments:
          </p>
          <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
            <li>Positivism → Deductive (test theories)</li>
            <li>Interpretivism → Inductive (build theory from data)</li>
            <li>Pragmatism → Abductive (iterate between data and theory)</li>
          </ul>
          <p style={{ marginTop: '0.5rem' }}>
            <em>Note: These are guidelines, not rules. You can justify different combinations.</em>
          </p>
        </div>
      </div>
      
      {warnings.length > 0 && warnings.map((warning, idx) => (
        <GuardrailWarning key={idx} type={warning.type} title={warning.type === 'error' ? 'Required' : 'Note'}>
          {warning.message}
        </GuardrailWarning>
      ))}
      
      <NavigationButtons />
    </div>
  );
};

export default Step2_TheoryApproach;
